import React from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { computeComparisonTimePeriod, getLastWeek } from 'src/utils/dateformatting';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  KeyMetricsData,
  KeyMetricsFilterAdapter,
  AccuracyKeyMetricsData
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/KeyMetricsAdapter';
import { ChartDataAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { useProductEntity, useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import { useContentService } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useContentService';
import { ContentMetricType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import colors from 'src/utils/colors';
import { TextLinearProgress } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/styled/BarComponents';
import { formatAccuracyData } from './utils';
import {
  ContentSectionId,
  titleToId
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import { useScrollToId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useScrollToId';
import { RightArrow } from 'src/components/SvgIcons/SvgIcons';
import {
  AccuracyFilters,
  useAccuracyFilterStore
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/AccuracyFilterStore';
import TextArrow from '../TextArrow';
import { ContentAccuracyTitles } from '../ContentBarPercent/utils';

const getFilterKey = (name: ContentAccuracyTitles) => {
  switch (name) {
    case ContentAccuracyTitles.Titles:
      return 'titleInaccuracies';
    case ContentAccuracyTitles.BulletPoints:
      return 'bulletInaccuracies';
    case ContentAccuracyTitles.Descriptions:
      return 'descriptionInaccuracies';
    case ContentAccuracyTitles.Images:
      return 'imageInaccuracies';
    case ContentAccuracyTitles.Videos:
      return 'videoInaccuracies';
    default:
      return '';
  }
};

const PercentBars = ({ primaryData }: { primaryData: [string, number, string, number][] }) => {
  const { isProductEntity } = useProductEntity();
  const scrollToId = useScrollToId<typeof ContentSectionId>();
  const { setFilter, setAllFilters } = useAccuracyFilterStore();

  const handleViewLowUtilizationClick = async (filterKey: keyof AccuracyFilters | '', type?: ContentSectionId) => {
    if (type) {
      await scrollToId(type);
    }
    if (filterKey === '') {
      await scrollToId(ContentSectionId.TableGrid);
      setAllFilters();
      return;
    }

    await scrollToId(ContentSectionId.TableGrid);
    setFilter(filterKey, true);
  };

  return (
    <Flex flexDirection="column" gap={24}>
      {primaryData.map((datum, index) => {
        const [name, value, details, lowProducts] = datum;
        return (
          <Flex
            key={index}
            gap={28}
            sx={{
              height: '72px',
              alignItems: 'center'
            }}
          >
            <TextLinearProgress mainValue={{ value, color: colors.teal }} />
            <Flex
              width="393px"
              flexDirection="column"
              sx={{
                flexShrink: 0,
                justifyContent: 'center',
                '& .MuiTypography-root': {
                  lineHeight: '24px'
                }
              }}
            >
              <Text variant="body1" sx={{ fontWeight: 500 }}>
                {name}
              </Text>
              <Text
                variant="body2"
                sx={{
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: isProductEntity ? 'pointer' : 'default',
                  '&:hover': {
                    textDecoration: isProductEntity ? 'underline' : 'none',
                    '& .hover-arrow': {
                      transform: 'translateX(2.5px)'
                    }
                  }
                }}
                onClick={() => {
                  if (isProductEntity) {
                    handleViewLowUtilizationClick(getFilterKey(name as ContentAccuracyTitles), titleToId(name));
                  }
                }}
              >
                {details}
                {isProductEntity && (
                  <RightArrow
                    style={{
                      marginLeft: '4px',
                      height: '16px',
                      width: '16px',
                      transition: 'all 0.2s ease-in-out'
                    }}
                    className="hover-arrow"
                  />
                )}
              </Text>
              {!isProductEntity && (
                <TextArrow onClick={() => handleViewLowUtilizationClick(getFilterKey(name as ContentAccuracyTitles))}>
                  View <span style={{ fontWeight: 500, margin: '0 4px' }}>{lowProducts.toLocaleString()} products</span>{' '}
                  with inaccuracies
                </TextArrow>
              )}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};

const AccuracyBarPercent = ({ forceLoading }: { forceLoading?: boolean }) => {
  const { getContentScoresKeyMetrics } = useContentService();
  const retailerId = useRetailerId();
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);
  const weekIds = allWeekIdsByRetailerId[retailerId];
  const entityName = useAppSelector((state) => state.entityService.mainEntity?.name);
  const { isProductEntity } = useProductEntity();
  const filters = useAccuracyFilterStore((state) => state.filters);
  const comparisonTimeSelection = useAppSelector((state) => state.comparisonTimePeriod.id);

  const comparisonTimePeriod = computeComparisonTimePeriod(
    weekIds,
    {
      startWeek: getLastWeek(weekIds),
      endWeek: getLastWeek(weekIds)
    },
    comparisonTimeSelection
  );

  return (
    <Flex flexDirection="column" gap={8}>
      <BeaconProChart
        name={`content-accuracy-bar-percent-key-metrics`}
        forceLoading={forceLoading}
        queryFn={async () => {
          const data = await getContentScoresKeyMetrics(
            ContentMetricType.ContentAccuracyScore,
            {
              startWeek: getLastWeek(weekIds),
              endWeek: getLastWeek(weekIds)
            },
            {
              startWeek: comparisonTimePeriod.startWeek,
              endWeek: comparisonTimePeriod.endWeek
            },
            {
              filterTitleInaccuracies: filters.titleInaccuracies,
              filterBulletPointsInaccuracies: filters.bulletInaccuracies,
              filterDescriptionInaccuracies: filters.descriptionInaccuracies,
              filterImageInaccuracies: filters.imageInaccuracies,
              filterVideoInaccuracies: filters.videoInaccuracies
            }
          );
          return new KeyMetricsFilterAdapter(data, {
            descriptionScore: ContentAccuracyTitles.Descriptions,
            featureBulletsScore: ContentAccuracyTitles.BulletPoints,
            imagesScore: ContentAccuracyTitles.Images,
            titleScore: ContentAccuracyTitles.Titles,
            videosScore: ContentAccuracyTitles.Videos,
            contentAccuracyScore: ContentAccuracyTitles.TotalContentAccuracy
          });
        }}
      >
        <BeaconProChart.Header>
          <BeaconProChart.Title>
            <Text variant="h4">Content Accuracy</Text>
          </BeaconProChart.Title>
        </BeaconProChart.Header>
        <BeaconProChart.Subtitle
          render={(data: ChartDataAdapter<KeyMetricsData>) => {
            const { primaryOverallScore } = data.transform();
            const accuracyScore = Math.round(primaryOverallScore * 100);
            return (
              <Text variant="body1">
                {accuracyScore}% of the content on {isProductEntity ? "this product's page " : 'product pages from '}
                {isProductEntity ? '' : entityName} accurately matched the master content last week.
              </Text>
            );
          }}
        />
        <BeaconProChart.Visualization
          render={(data: ChartDataAdapter<AccuracyKeyMetricsData>) => {
            const { rawData } = data.transform();
            const formattedData = formatAccuracyData(rawData, isProductEntity);
            const primaryData = formattedData.map((item) => [
              item.title,
              item.primary,
              item.details,
              item.lowProducts
            ]) as [string, number, string, number][];

            return (
              <Flex
                flexDirection="column"
                sx={{
                  marginTop: '32px'
                }}
              >
                <PercentBars primaryData={primaryData} />
              </Flex>
            );
          }}
        />
      </BeaconProChart>
    </Flex>
  );
};

export default AccuracyBarPercent;
